import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { Trans } from 'react-i18next'
import { Card } from 'shared/components/Card'
import LinkCTA from 'shared/components/Links/LinkCTA'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ProductQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts, media } from 'shared/lib'
import styled from 'styled-components'

const TP = 'productTemplate.components.ShippingAndReturnsDropdown'

const Terms = styled.div`
  ${fonts.BODY_TEXT}
  line-height: 18px;
  color: ${colors.FC2_BLACK};
  margin-bottom: 20px;

  ${media.large`
    &:last-child {
      margin-bottom: 30px;
    }
  `}
`

const Title = styled.p`
  ${fonts.SUBTITLE_3}
  margin: 0;
  ${media.large`
    &:first-child {
      margin-top: -10px;
    }
  `}
`

const StyledLink = styled(LinkCTA)`
  ${fonts.LINK}
  line-height: 18px;
`

const StyledCard = styled(Card)`
  border: none;
  ${media.large`
    margin: 30px 0 60px;
    border-bottom: 1px solid ${colors.FC2_LIGHTEST_GREY};
    border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  `}
`

export const ShippingAndReturnsDropdown: FC = () => {
  const tv = useTranslation()
  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const isFcShippingNoticeEnabled = useFeatureFlag(FeatureFlag.FC_SHIPPING_NOTICE)
  const isHolidayShippingCopyUpdate2024 = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_HOLIDAY_SHIPPING_COPY_UPDATE_2024,
  )
  const isReturnsCopyEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_PDP_RETURNS_COPY)
  const { countryName } = useShoppingRegionContext()

  const determineDeliveryTerms = () => {
    if (countryName === 'Japan') {
      return (
        <>
          <p>
            {t(
              `${TP}.shippingAndReturnsTermsJP1`,
              'Delivery and processing speeds vary by pricing options. All delivery times are estimates and are not guaranteed. Shipments may be affected by weather-related delays, carrier limitations or other events outside of our control. ',
            )}
          </p>
          <p>
            {t(
              `${TP}.shippingAndReturnsTermsJP2`,
              '“Fastest to You” orders are pre-verified and ready to ship from a Flight Club facility.',
            )}
          </p>
          <p>
            {t(
              `${TP}.shippingAndReturnsTermsJP3`,
              '"Lowest Price" orders may ship to Flight Club first for verification.',
            )}
          </p>
          {isHolidayShippingCopyUpdate2024 && (
            <>
              <p>
                {t(
                  `${TP}.shippingAndReturnsTermsJP4`,
                  `If you place an order by December 19th at 11 AM local time for an item labeled 'Fastest To You' and select the 'Standard' delivery method, you can expect your item to be delivered on or before December 24th.`,
                )}
              </p>
              <p>
                {t(
                  `${TP}.shippingAndReturnsTermsJP5`,
                  `If you place an order by December 22nd at 11 AM local time for an item labeled 'Fastest To You' and select the 'Next Day' delivery method, you can expect your item to be delivered on or before December 24th. Please note that not every item is eligible for Next Day delivery.`,
                )}
              </p>
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          <p>
            {t(
              `${TP}.shippingAndReturnsTerms1`,
              'Delivery and processing speeds vary by pricing options. The following shipping estimates apply only to the contiguous US and exclude deliveries to PO boxes and military bases. All delivery times are estimates and are not guaranteed. Shipments may be affected by weather-related delays, carrier limitations or other events outside of our control.',
            )}

            {isFcShippingNoticeEnabled &&
              t(
                `${TP}.shippingAndReturnsTerms1Tariff`,
                ' In addition, recent changes to US tariffs and Customs policy may result in delivery delays.',
              )}
          </p>
          <p>
            {t(
              `${TP}.shippingAndReturnsTerms2`,
              '"Lowest Price" orders may ship to Flight Club first for verification and typically take 7-12 business days (M-F, excluding holidays) to reach you. It will typically take 3-5 business days (M-F, excluding holidays) for a "Fastest To You" item, ordered before 11:00 AM local time with standard shipping, to be delivered.',
            )}
          </p>
          {isHolidayShippingCopyUpdate2024 && (
            <>
              <p>
                {t(
                  `${TP}.shippingAndReturnsTermsTemp3`,
                  `If you place an order by December 19th at 11 AM local time for an item labeled 'Fastest To You' and select the 'Standard' delivery method, you can expect your item to be delivered on or before December 24th.`,
                )}
              </p>
              <p>
                {t(
                  `${TP}.shippingAndReturnsTermsTemp4`,
                  `If you place an order by December 22nd at 11 AM local time for an item labeled 'Fastest To You' and select the 'Next Day' delivery method, you can expect your item to be delivered on or before December 24th. Please note that not every item is eligible for Next Day delivery.`,
                )}
              </p>
            </>
          )}
          <p>
            {t(
              `${TP}.shippingAndReturnsTermsAlt5`,
              'Estimated delivery times do not apply to international orders. International customers are responsible for any additional fees or taxes after an item ships.',
            )}
          </p>
        </>
      )
    }
  }

  return (
    <StyledCard
      data-testid="shippingAndReturnsCard"
      qaAttr={ProductQA.DeliveryAndReturns}
      title={t(`${TP}.shippingReturns`, 'Shipping & Returns')}
    >
      <Title
        data-qa={ProductQA.DeliveryAndReturnsDeliveryTitle}
        data-testid="shippingAndReturnsTitle"
      >
        {t(`${TP}.delivery`, 'Delivery')}
      </Title>
      <Terms
        data-qa={ProductQA.DeliveryAndReturnsDeliveryTerms}
        data-testid="shippingAndReturnsTerms"
      >
        {isReturnsCopyEnabled && countryName !== 'Japan' ? (
          <>
            <p>
              {t(
                `${TP}.onlineReturnsCopyDelivery1`,
                'Shipping estimates apply to the contiguous US only and exclude deliveries to PO boxes, military bases, and international addresses. All delivery times are estimates and not guaranteed. Shipments may be affected by weather, carrier limitations, or other events outside our control.',
              )}
            </p>
            <ul>
              <li>
                {t(
                  `${TP}.onlineReturnsCopyDeliveryBullet1`,
                  '"Lowest Price" orders may ship to Flight Club first for verification and typically take 7-12 business days (M-F, excluding holidays) to be delivered.',
                )}
              </li>
              <li>
                {t(
                  `${TP}.onlineReturnsCopyDeliveryBullet2`,
                  '"Fastest to You" orders placed before 11:00 AM local time typically take 3–5 business days (M–F, excluding holidays) to be delivered with standard shipping.',
                )}
              </li>
            </ul>
            <p>
              {t(
                `${TP}.onlineReturnsCopyDelivery2`,
                'International customers may be responsible for additional fees or taxes after an item ships.',
              )}
            </p>
          </>
        ) : isMarketPricing ? (
          determineDeliveryTerms()
        ) : (
          <>
            <p>
              {t(
                `${TP}.shippingAndReturnsTerms4`,
                'Processing and delivery times may vary based on item and location. Orders can take up to seven business days to process. Expedited shipping available on qualified orders in checkout.',
              )}
            </p>
            <p>
              {t(
                `${TP}.shippingAndReturnsTerms5`,
                'International customers are responsible for any additional fees or taxes after an item ships. Estimated delivery times do not apply to international orders.',
              )}
            </p>
          </>
        )}
      </Terms>
      <Title
        data-qa={ProductQA.DeliveryAndReturnsReturnsTitle}
        data-testid="shippingAndReturnsTitle"
      >
        {t(`${TP}.returns`, 'Returns')}
      </Title>
      <Terms
        data-qa={ProductQA.DeliveryAndReturnsReturnsTerms}
        data-testid="shippingAndReturnsTerms"
      >
        {isReturnsCopyEnabled ? (
          <>
            <p>
              {t(
                `${TP}.onlineReturnsCopyReturns1`,
                'For eligible items, you have 30 days after receiving your item to request a return.',
              )}
            </p>
            <p>
              <Trans
                i18nKey={`${TP}.onlineReturnsCopyReturns2`}
                t={tv.t}
                defaults="If you have any questions or concerns with regard to sizing or condition of a specific product on our site, please <0>contact us</0> before purchasing."
                components={[<a href="https://support.flightclub.com/hc/en-us/requests/new" />]}
              />
            </p>
            <StyledLink href="/shipping">{t(`${TP}.learnMore`, 'Learn more')}</StyledLink>
          </>
        ) : (
          <>
            <p>{t(`${TP}.shippingAndReturnsTerms6`, 'All sales with Flight Club are final.')}</p>
            <p>
              <Trans
                i18nKey={`${TP}.shippingAndReturnsTerms7`}
                t={tv.t}
                defaults="If you have any questions or concerns with regard to sizing or condition of a specific product on our site, please <0>contact us</0> before purchasing."
                components={[<a href="https://support.flightclub.com/hc/en-us/requests/new" />]}
              />
            </p>
            <StyledLink href="/shipping">{t(`${TP}.learnMore`, 'Learn more')}</StyledLink>
          </>
        )}
      </Terms>
    </StyledCard>
  )
}

export default ShippingAndReturnsDropdown
